import React from 'react';
import {Box, Grid, Typography} from '@mui/material';
import {Panel} from '../../components/Panel/Panel';
import {RichEditor} from '../../components/RichEditor/RichEditor';
import {RiskNotesSelector} from './RiskNotesSelector';
import {useAutoField, useAutoForm} from '../../components/AutoForm/useAutoForm';
import {outcomeRisk, RiskNotesContextProps, riskNotesFormType} from './riskNotesFields';
import {themeColors} from '../../theme/Theme';
import {Prompt} from '../../components/Prompt/Prompt';
import {riskNotesFormToVariables} from './RiskNotesFormHelpers';
import {useRiskNotesMutations} from './riskNotesQueries';
import {useOnUpdate} from '../../hooks/useOnUpdate';
import {UPDATES} from '../../shared/UPDATES';

export const RiskNotesOutcomeContact = ({
  contactId,
  risk,
}: {contactId: string} & RiskNotesContextProps) => {
  const {values} = useAutoForm<riskNotesFormType>();
  const [loading, setLoading] = React.useState(false);
  const {upsertRiskProfile} = useRiskNotesMutations();
  const [{value: noteValue}, {error: noteError}, {setValue: setNoteValue}] = useAutoField<
    outcomeRisk['notes']
  >(`outcomeRisk.${contactId}.notes`);
  const [{value: scoreValue}, {error: scoreError}, {setValue: setScore}] = useAutoField<
    outcomeRisk['score']
  >(`outcomeRisk.${contactId}.score`);
  const contact = risk.reviewDetails?.contacts?.find((c) => c.id === contactId);
  useOnUpdate(() => {
    setLoading(true);
    const variables = riskNotesFormToVariables(
      [contactId],
      values,
      risk?.reviewDetails?.id || ''
    );
    upsertRiskProfile({
      variables,
    }).finally(async () => {
      setLoading(false);
    });
  }, [noteValue, scoreValue]);

  const riskProfile = risk.reviewDetails?.riskProfiles.find(
    (rp) => rp.contact.id === contactId
  );
  const atrId = riskProfile?.initialRiskToleranceQuestionnaire?.id;

  if (!contact) {
    return (
      <Prompt title="Error loading contact" content="Contact not found" variant="error" />
    );
  }

  return (
    <>
      <Panel
        color={themeColors.amber}
        title={`Risk outcome for ${contact.simplifiedName}`}
      >
        <Grid container spacing={4}>
          <Grid item xs={5}>
            <Box p={2}>
              <Box
                mb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h4">
                  Please select a risk profile for {contact.simplifiedName}
                </Typography>
              </Box>

              <RiskNotesSelector
                currentScore={scoreValue}
                readOnly={false}
                onSelect={(selectedValue) => {
                  setScore(UPDATES._1007, selectedValue);
                }}
                error={scoreError}
              />
            </Box>
          </Grid>
          <Grid item xs={7}>
            <RichEditor
              formikId={`outcomeRisk.${contactId}.notes`}
              error={noteError}
              placeholder="Rationale for risk outcome"
              onSave={async (reason) => {
                setNoteValue(UPDATES._1006, reason);
              }}
              initialValue={noteValue}
            />
          </Grid>
        </Grid>
      </Panel>
    </>
  );
};
